import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useParams } from "react-router-dom";
import { msmartAxios } from "../../../api/axios";
import { useAuthContext } from "../../../hooks/useAuthContext";
import { useToast } from "../../../context/ToastContext";
import { formatDistanceToNow } from "date-fns";
import ExcelJS from "exceljs";
import { getErrorMessage } from "../../../utils/errorMessage";

function format12h(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function sanitizeCsvValue(v) {
  const s = String(v ?? "");
  // escape quotes
  const escaped = s.replace(/"/g, '""');
  // wrap if contains comma/newline/quote
  if (/[",\n]/.test(escaped)) return `"${escaped}"`;
  return escaped;
}

function makeFileName(prefix = "Leads", ext = "csv") {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${prefix}_${y}-${m}-${day}.${ext}`;
}


function SkeletonRows({ rows = 10 }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i}>
          <td><div className="skeleton-box" style={{ height: 14, width: 24 }} /></td>
          <td><div className="skeleton-box" style={{ height: 14, width: "45%" }} /></td>
          <td><div className="skeleton-box" style={{ height: 14, width: "60%" }} /></td>
          <td><div className="skeleton-box" style={{ height: 14, width: 90 }} /></td>
          <td><div className="skeleton-box" style={{ height: 14, width: 140 }} /></td>
          <td><div className="skeleton-box" style={{ height: 32, width: 70, borderRadius: 12 }} /></td>
        </tr>
      ))}
    </>
  );
}

export default function ManagerLeadList() {
  const { user } = useAuthContext();
  const { teamId } = useParams();
  const { notifyError, notifySuccess } = useToast();

  // ===== members =====
  const [members, setMembers] = useState([]);

  // ===== cursor pagination =====
  const DEFAULT_LIMIT = 26;
  const [limit, setLimit] = useState(DEFAULT_LIMIT); // 0 => All
  const [page, setPage] = useState(1);
  const [pageCursors, setPageCursors] = useState({ 1: null });
  const [hasMore, setHasMore] = useState(true);

  const [isLoading, setIsLoading] = useState(true);
  const abortControllerRef = useRef(null);

  // ===== data =====
  const [rows, setRows] = useState([]); // current page rows

  // ===== filter UI =====
  const [searchName, setSearchName] = useState("");
  const [searchStatus, setSearchStatus] = useState("");

  const [debouncedName, setDebouncedName] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedName(searchName.trim()), 350);
    return () => clearTimeout(t);
  }, [searchName]);

  // ===== ALL progress =====
  const allAbortRef = useRef(null);
  const [allProgress, setAllProgress] = useState({ running: false, count: 0 });

  // ===== modal remark =====
  const [selectedLead, setSelectedLead] = useState(null);
  const [editedRemark, setEditedRemark] = useState("");
  const [saving, setSaving] = useState(false);

  const getNameInTeam = (username) => {
    const m = members.find((x) => String(x.username) === String(username));
    return m ? m.nameInTeam : "-";
  };

  const buildUrl = ({ cursor, limit, status }) => {
    let url = `/api/msmart/manager/leads/cursor/${teamId}?limit=${limit}`;
    if (cursor) url += `&cursor=${encodeURIComponent(cursor)}`;
    if (status) url += `&status=${encodeURIComponent(status)}`;
    return url;
  };

  const fetchPage = async (targetPage, overrideLimit = null) => {
    let controller = null;
    try {
      setIsLoading(true);

      const currentLimit = overrideLimit ?? limit;

      // cancel previous
      if (abortControllerRef.current) abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();
      controller = abortControllerRef.current;

      const cursorForPage = pageCursors[targetPage];
      const url = buildUrl({
        cursor: cursorForPage,
        limit: currentLimit,
        status: searchStatus || "",
      });

      const res = await msmartAxios.get(url, {
        headers: { accessToken: user.token },
        signal: abortControllerRef.current.signal,
      });

      const { rows: newRows, nextCursor, members: mem } = res.data || {};

      // members only need once, but backend may return every time (ok)
      if (Array.isArray(mem) && mem.length) setMembers(mem);

      setRows(Array.isArray(newRows) ? newRows : []);

      if (nextCursor) {
        setPageCursors((prev) => ({ ...prev, [targetPage + 1]: nextCursor }));
        setHasMore(true);
      } else {
        setHasMore(false);
      }

      setPage(targetPage);
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      notifyError(getErrorMessage(err, "Unable to fetch leads."));
    } finally {
      if (abortControllerRef.current === controller) setIsLoading(false);
    }
  };

  const fetchAll = async () => {
    const controller = new AbortController();
    try {
      // cancel normal page fetch
      if (abortControllerRef.current) abortControllerRef.current.abort();
      abortControllerRef.current = null;

      // cancel previous all
      if (allAbortRef.current) allAbortRef.current.abort();
      allAbortRef.current = controller;

      setIsLoading(true);
      setAllProgress({ running: true, count: 0 });

      const batchLimit = 200;
      let cursor = null;
      let all = [];

      while (true) {
        const url = buildUrl({
          cursor,
          limit: batchLimit,
          status: searchStatus || "",
        });

        const res = await msmartAxios.get(url, {
          headers: { accessToken: user.token },
          signal: controller.signal,
        });

        const batchRows = res.data?.rows || [];
        if (Array.isArray(res.data?.members) && res.data.members.length) {
          setMembers(res.data.members);
        }

        all = all.concat(batchRows);
        setAllProgress({ running: true, count: all.length });

        const nextCursor = res.data?.nextCursor;
        if (!nextCursor) break;
        cursor = nextCursor;
      }

      setRows(all);
      setPage(1);
      setPageCursors({ 1: null });
      setHasMore(false);
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      notifyError(getErrorMessage(err, "Error fetching all leads"));
    } finally {
      if (allAbortRef.current === controller) {
        setIsLoading(false);
        setAllProgress({ running: false, count: 0 });
      }
    }
  };

  const cancelFetchAll = () => {
    if (allAbortRef.current) allAbortRef.current.abort();
  };

  // initial + whenever status/limit changes
  useEffect(() => {
    setPage(1);
    setPageCursors({ 1: null });

    if (limit === 0) {
      setHasMore(false);
      fetchAll();
    } else {
      setHasMore(true);
      fetchPage(1, limit);
    }
    // eslint-disable-next-line
  }, [teamId, user.token, limit, searchStatus]);

  // local filter for member name (since server filter is status only)
  const filteredRows = useMemo(() => {
    const q = debouncedName.toLowerCase();
    if (!q) return rows;

    return rows.filter((lead) => {
      const member = getNameInTeam(lead.username).toLowerCase();
      return member.includes(q);
    });
    // eslint-disable-next-line
  }, [rows, debouncedName, members]);

  // KPI
  const kpi = useMemo(() => {
    const counts = { total: filteredRows.length, followUp: 0, booking: 0, closed: 0, rejected: 0, noStatus: 0 };
    for (const l of filteredRows) {
      if (l.status === "Follow Up") counts.followUp++;
      else if (l.status === "Booking") counts.booking++;
      else if (l.status === "Closed") counts.closed++;
      else if (l.status === "Rejected") counts.rejected++;
      else if (l.status === "No Status") counts.noStatus++;
    }
    return counts;
  }, [filteredRows]);

  const onPrev = () => {
    if (limit === 0) return;
    if (page <= 1) return;
    fetchPage(page - 1);
  };

  const onNext = () => {
    if (limit === 0) return;
    if (!hasMore) return;
    fetchPage(page + 1);
  };

  const onChangeLimit = (val) => {
    const newLimit = Number(val);
    setLimit(newLimit);
    setPage(1);
    setPageCursors({ 1: null });
  };

  const openRemarkModal = (lead) => {
    setSelectedLead(lead);
    setEditedRemark("");
  };

  const resetModal = () => {
    setSelectedLead(null);
    setEditedRemark("");
    setSaving(false);
  };

  const saveEditedRemark = async () => {
    if (!selectedLead) return;

    if (!editedRemark.trim()) {
      notifyError("Remark kosong bro. Isi dulu.");
      return;
    }

    setSaving(true);

    const payload = {
      remark: selectedLead.remark,
      updatedRemark: editedRemark,
      remarkDate: new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    };

    try {
      const res = await msmartAxios.put(
        `/api/msmart/manager/edit/remark/${teamId}/${selectedLead.id}`,
        payload,
        { headers: { accessToken: user.token } }
      );

      if (res?.data?.succ) {
        notifySuccess(res.data.succ);

        // close bootstrap modal
        document.getElementById("mgrlead_closemodal")?.click();

        // refresh current page / all
        if (limit === 0) await fetchAll();
        else await fetchPage(page);

        resetModal();
      } else {
        notifyError(res?.data?.error || "Unable to update remark");
      }
    } catch (err) {
      notifyError(getErrorMessage(err, "Unable to update remark"));
    } finally {
      setSaving(false);
    }
  };

  const exportCurrentToCSV = () => {
    try {
      if (!filteredRows.length) {
        notifyError("Tak ada data untuk export.");
        return;
      }
  
      const headers = [
        "#",
        "Team Member",
        "Username",
        "Lead Name",
        "Phone",
        "Status",
        "Follow Up Date",
        "Created At",
        "Last Update",
        "Remark",
      ];
  
      const lines = [];
      lines.push(headers.map(sanitizeCsvValue).join(","));
  
      filteredRows.forEach((lead, idx) => {
        const row = [
          idx + 1,
          getNameInTeam(lead.username),
          lead.username,
          lead.name || "",
          `${lead.country || ""}${lead.phone || ""}`,
          lead.status || "",
          lead.followUpDate ? format12h(lead.followUpDate) : "",
          lead.createdAt ? format12h(lead.createdAt) : "",
          lead.updatedAt ? format12h(lead.updatedAt) : "",
          lead.remark || "",
        ];
        lines.push(row.map(sanitizeCsvValue).join(","));
      });
  
      const csv = "\uFEFF" + lines.join("\n"); // BOM for Excel
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = makeFileName(`ManagerLeads_Team${teamId}_Page${page}`, "csv");
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      notifyError(getErrorMessage(err, "Export CSV failed."));
    }
  };

  const exportCurrentToExcel = async () => {
    try {
      if (!filteredRows.length) {
        notifyError("Tak ada data untuk export.");
        return;
      }
  
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Leads");
  
      ws.columns = [
        { header: "#", key: "idx", width: 6 },
        { header: "Team Member", key: "member", width: 22 },
        { header: "Username", key: "username", width: 18 },
        { header: "Lead Name", key: "name", width: 22 },
        { header: "Phone", key: "phone", width: 18 },
        { header: "Status", key: "status", width: 12 },
        { header: "Follow Up Date", key: "followUp", width: 20 },
        { header: "Created At", key: "createdAt", width: 20 },
        { header: "Last Update", key: "updatedAt", width: 20 },
        { header: "Remark", key: "remark", width: 35 },
      ];
  
      filteredRows.forEach((lead, i) => {
        ws.addRow({
          idx: i + 1,
          member: getNameInTeam(lead.username),
          username: lead.username,
          name: lead.name || "",
          phone: `${lead.country || ""}${lead.phone || ""}`,
          status: lead.status || "",
          followUp: lead.followUpDate ? format12h(lead.followUpDate) : "",
          createdAt: lead.createdAt ? format12h(lead.createdAt) : "",
          updatedAt: lead.updatedAt ? format12h(lead.updatedAt) : "",
          remark: lead.remark || "",
        });
      });
  
      // header style sikit
      ws.getRow(1).font = { bold: true };
  
      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
  
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = makeFileName(`ManagerLeads_Team${teamId}_Page${page}`, "xlsx");
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      notifyError(getErrorMessage(err, "Export Excel failed."));
    }
  };

  return (
    <div className="container py-6 ss-page">
      {/* Header */}
      <div className="row justify-content-center text-start">
        <div className="col-lg-12">
          <h1 className="header-title mb-1">M-Smart</h1>
          <div className="text-muted" style={{ fontSize: 14 }}>
          Sales Activity Management.
          </div>
        </div>
      </div>

      {/* Segment */}
      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="msmart-segment my-4">
            <NavLink to="#" className={() => "msmart-seg-link active"}>
              Team
            </NavLink>
            <NavLink
              to={`/msmart/manager/database/${teamId}`}
              className={({ isActive }) => `msmart-seg-link ${isActive ? "active" : ""}`}
            >
              Personal
            </NavLink>
          </div>
        </div>
      </div>

      <div className="card text-center mt-4 ds-card">
        {/* Tabs (kekal) */}
        <div className="card-header text-start">
          <ul className="nav nav-tabs card-header-tabs flex-wrap">
            <li className="nav-item">
              <Link className="nav-link" to={`/msmart/team/summary/${teamId}`}>
                Summary
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to={`/msmart/team/summary/${teamId}?view=analysis`}>Analysis</Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to={`/msmart/team/manage/${teamId}`}>
                Manage
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to={`/msmart/team/activity/${teamId}`}>
                Activities
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to={`/msmart/team/followup/${teamId}`}>
                Follow Up
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link active" to="#">
                Leads
              </Link>
            </li>
          </ul>
        </div>

        <div className="card-body text-start">
          {/* CONTROL BAR */}
          <div className="card mb-3">
            <div className="card-body p-3">
              <div className="d-flex flex-column flex-lg-row gap-2 justify-content-between align-items-lg-center">
                <div className="d-flex flex-column flex-md-row gap-2 flex-grow-1">
                  <div className="form-floating flex-grow-1">
                    <input
                      id="mgrSearchMember"
                      className="form-control"
                      placeholder="Search by Team Member"
                      value={searchName}
                      onChange={(e) => setSearchName(e.target.value)}
                    />
                    <label htmlFor="mgrSearchMember">Search by Team Member</label>
                  </div>

                  <div className="form-floating flex-grow-1">
                    <select
                      id="mgrSearchStatus"
                      className="form-select"
                      value={searchStatus}
                      onChange={(e) => setSearchStatus(e.target.value)}
                    >
                      <option value="">All Status</option>
                      <option value="No Status">No Status</option>
                      <option value="Follow Up">Follow Up</option>
                      <option value="Rejected">Rejected</option>
                      <option value="Booking">Booking</option>
                      <option value="Closed">Closed</option>
                    </select>
                    <label htmlFor="mgrSearchStatus">Status</label>
                  </div>
                </div>

                <div className="d-flex gap-2 justify-content-end align-items-center">
                  <div className="text-muted" style={{ fontSize: "0.85rem", fontStyle: "italic" }}>
                    Show leads / page
                  </div>

                  <select
                    className="form-select form-select-sm w-auto"
                    value={limit}
                    onChange={(e) => onChangeLimit(e.target.value)}
                    disabled={isLoading && allProgress.running}
                  >
                    <option value={26}>26</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={0}>All</option>
                  </select>

                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => (limit === 0 ? fetchAll() : fetchPage(1))}
                    disabled={isLoading}
                    title="Refresh"
                  >
                    <i className="bi bi-arrow-clockwise" />
                  </button>

                  <button
                    className="btn btn-sm btn-dark"
                    onClick={exportCurrentToExcel}
                    disabled={isLoading || filteredRows.length === 0}
                    title="Export current filtered list (Excel)"
                  >
                    <i className="bi bi-download me-2" />
                    Export
                  </button>

                </div>
              </div>

              {/* KPI chips */}
              <div className="d-flex flex-wrap gap-2 mt-3">
                <span className="ds-chip">
                  <i className="bi bi-list-check" /> Total: <b>{kpi.total}</b>
                </span>
                <span className="ds-chip">
                  <i className="bi bi-clock-history" /> Follow Up: <b>{kpi.followUp}</b>
                </span>
                <span className="ds-chip">
                  <i className="bi bi-calendar2-check" /> Booking: <b>{kpi.booking}</b>
                </span>
                <span className="ds-chip">
                  <i className="bi bi-x-circle" /> Rejected: <b>{kpi.rejected}</b>
                </span>
                <span className="ds-chip ds-chip-dark">
                  <i className="bi bi-cash-coin" /> Closed: <b>{kpi.closed}</b>
                </span>
              </div>

              {allProgress.running && (
                <div className="alert alert-info py-2 mt-3 d-flex justify-content-between align-items-center">
                  <div className="small">
                    Loading all leads... <strong>{allProgress.count}</strong> loaded
                  </div>
                  <button className="btn btn-sm btn-outline-secondary" onClick={cancelFetchAll}>
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Pagination top */}
          <div className="d-flex justify-content-end align-items-center mb-2">
            <button
              className="btn btn-sm btn-light me-2"
              onClick={onPrev}
              disabled={isLoading || page === 1 || limit === 0}
            >
              <i className="bi bi-caret-left" />
            </button>

            <span className="text-muted" style={{ fontSize: "0.9rem" }}>
              Page <b>{page}</b>
            </span>

            <button
              className="btn btn-sm btn-light ms-2"
              onClick={onNext}
              disabled={isLoading || !hasMore || limit === 0}
            >
              <i className="bi bi-caret-right" />
            </button>
          </div>

          {/* Table */}
          <div className="table-responsive" style={{ fontSize: "0.9rem" }}>
            <table className="table table-hover table-bordered text-center align-middle">
              <thead className="bg-light">
                <tr>
                  <th style={{ width: 60 }}>#</th>
                  <th className="text-start" style={{ minWidth: 180 }}>Team Member</th>
                  <th className="text-start" style={{ minWidth: 180 }}>Lead</th>
                  <th style={{ width: 140 }}>Status</th>
                  <th style={{ width: 200 }}>Last Update</th>
                  <th style={{ width: 110 }}>Action</th>
                </tr>
              </thead>

              <tbody>
                {isLoading ? (
                  <SkeletonRows rows={10} />
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-muted">
                      No leads found.
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((lead, idx) => (
                    <tr key={lead.id}>
                      <td>{idx + 1 + (limit === 0 ? 0 : (page - 1) * limit)}</td>

                      <td className="text-start">
                        <div className="fw-semibold">{getNameInTeam(lead.username)}</div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          @{lead.username}
                        </div>
                      </td>

                      <td className="text-start">
                        <div className="fw-semibold">{lead.name || "-"}</div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          {`${lead.country || ""}${lead.phone || ""}`}
                        </div>
                      </td>

                      <td>
                        <span className="badge bg-light text-dark ss-badge">
                          {lead.status || "N/A"}
                        </span>
                      </td>

                      <td>
                        <div>{format12h(lead.updatedAt)}</div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          ({formatDistanceToNow(new Date(lead.updatedAt), { addSuffix: true })})
                        </div>
                      </td>

                      <td>
                        <button
                          className="btn btn-sm btn-dark"
                          data-bs-toggle="modal"
                          data-bs-target="#mgrEditRemarkModal"
                          onClick={() => openRemarkModal(lead)}
                          title="Edit Remark"
                        >
                          <i className="bi bi-megaphone" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination bottom */}
          <div className="d-flex justify-content-end align-items-center mt-2">
            <button
              className="btn btn-sm btn-light me-2"
              onClick={onPrev}
              disabled={isLoading || page === 1 || limit === 0}
            >
              <i className="bi bi-caret-left" />
            </button>

            <span className="text-muted" style={{ fontSize: "0.9rem" }}>
              Page <b>{page}</b>
            </span>

            <button
              className="btn btn-sm btn-light ms-2"
              onClick={onNext}
              disabled={isLoading || !hasMore || limit === 0}
            >
              <i className="bi bi-caret-right" />
            </button>
          </div>
        </div>
      </div>

      {/* MODAL: Edit Remark */}
      <div
        className="modal fade"
        id="mgrEditRemarkModal"
        tabIndex="-1"
        aria-hidden="true"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content ss-modal ds-modal">
            <div className="modal-header ss-modal-header">
              <div>
                <div className="fw-bold">Edit Remark</div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Update remark untuk lead ni.
                </div>
              </div>

              <button
                type="button"
                id="mgrlead_closemodal"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={resetModal}
              />
            </div>

            {!selectedLead ? (
              <div className="modal-body text-start">
                <p className="text-muted mb-0">No details available.</p>
              </div>
            ) : (
              <>
                <div className="modal-body text-start" style={{ fontSize: "0.9rem" }}>
                  <div className="row g-2">
                    <div className="col-md-6">
                      <div className="small text-muted">Team Member</div>
                      <div className="fw-semibold">
                        {getNameInTeam(selectedLead.username)} (@{selectedLead.username})
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="small text-muted">Status</div>
                      <div className="fw-semibold">{selectedLead.status}</div>
                    </div>

                    <div className="col-md-6">
                      <div className="small text-muted">Lead</div>
                      <div className="fw-semibold">{selectedLead.name}</div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        {`${selectedLead.country}${selectedLead.phone}`}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="small text-muted">Follow Up Date</div>
                      <div className="fw-semibold">
                        {selectedLead.followUpDate ? format12h(selectedLead.followUpDate) : "No Follow Up"}
                      </div>
                      {selectedLead.followUpDate ? (
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          ({formatDistanceToNow(new Date(selectedLead.followUpDate), { addSuffix: true })})
                        </div>
                      ) : null}
                    </div>

                    <div className="col-md-6">
                      <div className="small text-muted">Created</div>
                      <div>{format12h(selectedLead.createdAt)}</div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        ({formatDistanceToNow(new Date(selectedLead.createdAt), { addSuffix: true })})
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="small text-muted">Last Update</div>
                      <div>{format12h(selectedLead.updatedAt)}</div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        ({formatDistanceToNow(new Date(selectedLead.updatedAt), { addSuffix: true })})
                      </div>
                    </div>
                  </div>

                  <hr />

                  <div className="small text-muted mb-1">Current Remark</div>
                  <div className="card mb-3">
                    <div className="card-body" style={{ whiteSpace: "pre-wrap" }}>
                      {selectedLead.remark ? selectedLead.remark : <span className="text-muted">No Remark</span>}
                    </div>
                  </div>

                  <div className="form-floating">
                    <textarea
                      id="mgrNewRemark"
                      className="form-control"
                      style={{ height: 140 }}
                      placeholder="Type your remark..."
                      value={editedRemark}
                      onChange={(e) => setEditedRemark(e.target.value)}
                    />
                    <label htmlFor="mgrNewRemark">Your Remark (New)</label>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    data-bs-dismiss="modal"
                    onClick={resetModal}
                    disabled={saving}
                  >
                    Close
                  </button>

                  <button
                    type="button"
                    className="btn btn-dark"
                    onClick={saveEditedRemark}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-save me-2" />
                        Save
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
